// src/client/notify.ts — Web Notifications API wrapper.
// Must be called from a user gesture to prompt for permission.

export type NotifyStatus = "unsupported" | "default" | "granted" | "denied";

export function notifyStatus(): NotifyStatus {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission as NotifyStatus;
}

/**
 * Request permission. Must be called from a user gesture (button click).
 * Returns the resulting status. No-op if already decided.
 */
export async function requestPermission(): Promise<NotifyStatus> {
  if (notifyStatus() !== "default") return notifyStatus();
  try {
    const r = await Notification.requestPermission();
    return r as NotifyStatus;
  } catch {
    return "denied";
  }
}

interface NotifyOptions {
  title: string;
  body?: string;
  /** When clicked, focus the existing tab if any (default true). */
  focusOnClick?: boolean;
  tag?: string;
  icon?: string;
}

/**
 * Show a notification if permission is granted. Returns the Notification
 * instance, or null if not shown. Silently does nothing for denied / default
 * — caller should `requestPermission()` first from a user gesture.
 */
export function show(opts: NotifyOptions): Notification | null {
  if (notifyStatus() !== "granted") return null;
  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
      icon: opts.icon,
      silent: false,
    });
    if (opts.focusOnClick !== false) {
      n.onclick = () => {
        try {
          window.focus();
        } catch {
          /* */
        }
        n.close();
      };
    }
    return n;
  } catch {
    return null;
  }
}
